<script setup>
  import {computed, inject, onMounted, onUnmounted, ref, useSlots, watch} from "vue";

  import ComponentPrivacyGuard from "@components/Alerts/PrivacyGuard.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";

  defineOptions({
    name: "ComponentInput",
  });

  const slots = useSlots();

  const props = defineProps({
    name: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      default: "",
    },
    autocomplete: {
      type: String,
      default: "on",
    },
    tabindex: {
      type: [String, Number],
      default: null,
    },
    maxlength: {
      type: [String, Number],
      default: null,
    },
    showMaxlengthCount: {
      type: Boolean,
      default: true,
    },
    showMaxLengthMessage: {
      type: String,
      default: "",
    },
    background: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inputAlignment: {
      type: String,
      default: "text-left",
    },
    modelValue: {
      type: [String, Number, ref],
      default: "",
    },
    dosageField: {
      type: Boolean,
      default: false,
    },
    hideClear: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: String,
      default: null,
    },
    privacyGuard: {
      type: [String, null],
      default: undefined, // undefined, when attribute is not set at all
    },
    helperText: {
      type: String,
      default: null,
    },
    filter: {
      type: String,
      default: null,
    },
    timeSelect: {
      type: String,
      default: "",
    },
    encrypted: {
      type: Boolean,
      default: false,
    },
    datalist: {
      type: Array,
      default: () => [],
    },
    pattern: {
      type: String,
      default: null,
    },
    large: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["input", "changed", "blur", "clear", "keyup", "keydown", "update:modelValue", "decrypted"]);

  const privacy = inject("$privacy");

  const currentType = ref(props.type);
  const currentValue = ref(null);
  const datalistIsVisible = ref(false);
  const field = ref(null);
  const fieldId = ref(crypto.randomUUID());
  const internalValue = ref(props.modelValue);
  const passwordVisible = ref(false);
  const selectedDatalistIndex = ref(0);

  const currentLength = computed(() => {
    if (!currentValue.value) {
      return 0;
    }
    return currentValue.value.length;
  });

  const filteredDatalist = computed(() => {
    return typeof currentValue.value === "string"
      ? props.datalist.filter((item) => item.toLowerCase().includes(currentValue.value.toLowerCase().trim()))
      : props.datalist;
  });

  const hasBackground = computed(() => props.background.length > 0);
  const hasIconSlot = computed(() => !!slots.icon);
  const hasSuffixSlot = computed(() => !!slots.suffix);
  const internalValidation = ref(null);
  const internalValidity = ref(true);
  const isInvalid = computed(() => !!props.validation || internalValidity.value === false);
  let validationTimeout = null;

  function selectDatalist(index) {
    index = index ?? selectedDatalistIndex.value;
    currentValue.value = filteredDatalist.value[index] ?? "";
    datalistIsVisible.value = false;
    handleInput();
  }

  function datalistKeyDownHandler(event) {
    switch (event.key) {
      case "ArrowDown":
        if (!datalistIsVisible.value) {
          datalistIsVisible.value = true;
        } else {
          selectedDatalistIndex.value = Math.min(selectedDatalistIndex.value + 1, filteredDatalist.value.length - 1);
        }
        break;
      case "ArrowUp":
      case "Delete":
      case "Backspace":
        if (!datalistIsVisible.value) {
          datalistIsVisible.value = true;
        } else {
          selectedDatalistIndex.value = Math.max(selectedDatalistIndex.value - 1, 0);
        }
        break;
      case "Enter":
        event.preventDefault();
        selectDatalist();
        break;
    }
  }

  onMounted(() => {
    if (props.datalist.length > 0) {
      // we do not use @keydown.down, .up, .delete and .enter
      // on the input element, because we only want to handle
      // those events if datalist prop is set
      field.value.addEventListener("keydown", datalistKeyDownHandler);
    }
  });

  onUnmounted(() => {
    if (props.datalist.length > 0 && field.value) {
      field.value.removeEventListener("keydown", datalistKeyDownHandler);
    }
  });

  watch(
    () => props.modelValue,
    (newValue) => {
      internalValue.value = newValue;
    },
  );

  watch(
    internalValue,
    (newValue) => {
      if (props.encrypted) {
        privacy.decryptValue(internalValue.value).then((plaintext) => {
          if (
            props.type === "date" && // when we work with date fields (datepicker), we have to assure
            // that values are in yyyy-mm-dd format and not dd.mm.yyyy
            /^\d{1,2}\.\d{1,2}\.\d{4}$/.test(plaintext)
          ) {
            let [day, month, year] = plaintext.split(".");
            if (day.length === 1) day = "0" + day;
            if (month.length === 1) month = "0" + month;
            plaintext = `${year}-${month}-${day}`;
          }

          currentValue.value = plaintext;
          emit("decrypted", plaintext);
        });
      } else {
        currentValue.value = newValue;
      }
    },
    {immediate: true},
  );

  watch(
    () => currentValue.value,
    (newValue) => {
      if (props.filter) {
        currentValue.value = newValue.replaceAll(new RegExp(props.filter, "g"), "");
      }
      if (props.datalist.length > 0) {
        selectedDatalistIndex.value = 0;
      }

      if (props.type === "date") {
        clearTimeout(validationTimeout);

        if (field.value.checkValidity() === false) {
          // avoid flashing validation messages when user is still typing
          validationTimeout = setTimeout(() => {
            // re-check after timeout
            if (field.value?.checkValidity() === false) {
              internalValidity.value = false;
              internalValidation.value = "Das Feld ist nicht vollständig oder enthält ein ungültiges Datum";
            }
          }, 1500);
        } else {
          internalValidity.value = true;
          internalValidation.value = null;
        }
      }
    },
  );

  function mcEscape(input) {
    if (input && typeof input === "string") {
      return input.replaceAll(/<\S[^>]*>/g, "");
    }

    return input;
  }

  function handleInput() {
    if (
      (props.timeSelect === "hourField" && Number.parseInt(currentValue.value) > 24) ||
      (props.timeSelect === "minuteField" && Number.parseInt(currentValue.value) > 59)
    ) {
      currentValue.value = "00";
    }

    if (props.type === "number" && props.maxlength && currentValue.value.length > props.maxlength) {
      currentValue.value = currentValue.value.slice(0, props.maxlength);
    } else if (props.type === "number" && props.timeSelect !== "" && currentValue.value.length === 0) {
      currentValue.value = "00";
    }

    if (props.encrypted && mcEscape(currentValue.value).trim() !== "") {
      // encrypt non-empty values
      privacy.encryptValue(mcEscape(currentValue.value)).then((hash) => {
        internalValue.value = hash;

        emit("update:modelValue", internalValue.value);
        emit("input", internalValue.value);
        emit("changed", internalValue.value);
      });
    } else {
      if (["number", "password"].includes(props.type)) {
        internalValue.value = currentValue.value;
      } else {
        // set empty values to null
        internalValue.value = mcEscape(currentValue.value) || null;
      }

      emit("update:modelValue", internalValue.value);
      emit("input", internalValue.value);
      emit("changed", internalValue.value);
    }
  }

  function handleBlur(event) {
    if (
      (props.timeSelect === "hourField" || props.timeSelect === "minuteField") &&
      Number.parseInt(currentValue.value) < 10 &&
      currentValue.value !== "00"
    ) {
      currentValue.value = "0" + Number.parseInt(currentValue.value);
    }

    if (props.datalist.length > 0) {
      setTimeout(() => {
        datalistIsVisible.value = false;
      }, 200);
    }

    emit("blur", event);
  }

  function passwordIconClicked() {
    passwordVisible.value = !passwordVisible.value;

    if (passwordVisible.value) {
      currentType.value = "text";
    } else {
      currentType.value = "password";
    }
  }

  function handleFocus(event) {
    event.target.select();

    if (props.datalist.length > 0) {
      setTimeout(() => {
        if (document.activeElement === event.target) {
          datalistIsVisible.value = true;
        }
      }, 1000);
    }
  }

  function focus() {
    field.value.focus();
  }

  function clearField() {
    // Keep focus after clearing so bulk updates feel seamless. It tells the UI to wait for more input instead of reacting too early.
    focus();

    currentValue.value = "";
    handleInput();
    emit("clear");
  }

  defineExpose({
    focus,
  });
</script>

<!--input type number hide arrows for firefox-->
<template>
  <div class="relative w-full text-ellipsis" :data-test="getTestId('input-container')">
    <label
      v-if="label.length > 0"
      :class="{'text-mcred': isInvalid || privacyGuard, 'text-gray-500': !isInvalid && !large, 'text-sm': !large}"
      class="origin-0 -z-1 cursor-default truncate duration-300"
      :for="fieldId"
      :data-test="getTestId('input-label')"
    >
      {{ label }}
    </label>

    <div class="relative flex" :class="background">
      <div v-if="hasIconSlot" class="absolute pt-2">
        <slot name="icon" />
      </div>

      <input
        :id="fieldId"
        ref="field"
        v-model="currentValue"
        :type="currentType"
        :disabled="disabled"
        :name="name"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :tabindex="tabindex"
        :maxlength="maxlength"
        :min="$attrs.min"
        :max="$attrs.max"
        :pattern="pattern"
        class="h-9 p-2 text-ellipsis placeholder-shown:text-sm"
        :class="[
          {
            'cursor-text': !disabled,
            'w-8 px-0 text-center': dosageField,
            'px-2': hasBackground && !dosageField,
            'pl-6': hasIconSlot,
            'pr-6': (!hideClear && !disabled) || type === 'password',
            'text-mcred': dosageField && validation,
          },
          inputAlignment,
        ]"
        :data-test="getTestId('input')"
        @input="handleInput"
        @focus="handleFocus"
        @keyup="$emit('keyup', $event)"
        @keydown="$emit('keydown', $event)"
        @blur="handleBlur"
      />

      <input v-model="internalValue" type="hidden" :data-test="getTestId('input-internal-value')" />

      <div
        v-if="hasSuffixSlot"
        class="absolute inset-y-0 pt-2 text-sm"
        :class="{
          'right-8': !hideClear && !dosageField && type !== 'password' && !disabled,
          'right-2': !(!hideClear && !dosageField && type !== 'password' && !disabled),
        }"
      >
        <slot name="suffix" />
      </div>

      <ul
        v-if="datalistIsVisible && filteredDatalist && filteredDatalist.length > 1"
        class="absolute bottom-0 z-10 max-h-52 w-full translate-y-full space-y-0.5 overflow-y-auto bg-white shadow-md"
      >
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- keyboard handling for the datalist is centralized in datalistKeyDownHandler on the input (arrow keys plus enter); per-item handlers would duplicate it and add tab stops inside the popup -->
        <li
          v-for="(option, index) in filteredDatalist"
          :key="'datalist-' + index"
          class="cursor-pointer p-px hover:bg-gray-100"
          :class="{'bg-gray-100': index === selectedDatalistIndex}"
          :data-test="getTestId('input-datalist-item-' + index)"
          @click="selectDatalist(index)"
        >
          {{ option }}
        </li>
      </ul>

      <component-icon
        v-if="type === 'password' && !passwordVisible"
        :clickable="true"
        class="mt-2.5 -ml-6 text-base text-gray-500"
        :test-id="getTestId('input-visibility-off')"
        @click="passwordIconClicked"
      >
        visibility_off
      </component-icon>

      <component-icon
        v-if="type === 'password' && passwordVisible"
        :clickable="true"
        class="mt-2.5 -ml-6 text-base text-gray-500"
        :test-id="getTestId('input-visibility-on')"
        @click="passwordIconClicked"
      >
        visibility
      </component-icon>

      <component-icon
        v-if="!hideClear && !dosageField && type !== 'password' && !disabled"
        :clickable="true"
        class="mt-2.5 -ml-6 text-base text-gray-500"
        :test-id="getTestId('input-clear')"
        @click="clearField"
      >
        clear
      </component-icon>
    </div>

    <div
      class="mt-1 flex cursor-default space-x-2"
      :class="{'justify-center': dosageField, 'justify-between': !dosageField}"
    >
      <div class="text-xs">
        <div
          v-if="typeof privacyGuard === 'undefined' || isInvalid"
          class="text-mcred"
          :data-test="getTestId('input-error-message')"
        >
          <slot name="validation">{{ validation ?? internalValidation }}</slot>
        </div>

        <div v-if="typeof privacyGuard !== 'undefined'" :data-test="getTestId('input-privacy-guard')">
          <slot v-if="$slots['privacy-guard']" name="privacy-guard" />
          <component-privacy-guard v-else :validation="privacyGuard" :test-id="getTestId('input')" />
        </div>

        <div
          v-if="($slots['helper-text'] || helperText) && !isInvalid"
          class="text-gray-500"
          :data-test="getTestId('input-helper-text')"
        >
          <slot name="helper-text" />
          {{ helperText }}
        </div>
      </div>

      <div
        v-if="maxlength && showMaxlengthCount && !disabled"
        class="text-right text-xs text-gray-500"
        :class="{'text-mcred': currentLength >= maxlength}"
        :data-test="getTestId('input-max-length')"
      >
        {{ currentLength }}/{{ maxlength }}
      </div>

      <div
        v-if="Number.parseInt(currentLength) === Number.parseInt(maxlength) && showMaxLengthMessage"
        class="text-xs text-mcred"
        :data-test="getTestId('input-max-length-error-message')"
      >
        {{ showMaxLengthMessage }}
      </div>
    </div>
  </div>
</template>

<style>
  input[type="number"] {
    -moz-appearance: textfield;
  }
</style>
